import {
  Box,
  Button,
  ColumnFilterPopover,
  FilterDropDown,
  FormControl,
  FormLabel,
  H2,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  MaterialTableWrapper,
  MaterialTableWrapperProps,
  MRT_PaginationState,
  MRT_Row,
  MRT_SortingState,
  MRT_TableOptions,
  ModalContainer,
  RowActionButton,
  TextInput,
  withThemeProvider,
} from '@ouster/webviz-react-components';

import { IconButton } from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import {
  MdClear,
  MdOutlinePublishedWithChanges,
  MdOutlineUnpublished,
  MdRefresh,
  MdSearch,
} from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router';

import { useArchitectSceneColumns } from '../../components/architectSceneTable/ArchitectSceneColumns';
import { useArchitectSceneFilterOptions } from '../../components/architectSceneTable/useArchitectSceneFilterOptions';
import {
  useArchitectSceneListQuery,
  useDownloadArchitectSceneFiles,
  usePublishArchitectScene,
  useUnpublishArchitectScene,
  useUpdateArchitectSceneTitle,
} from '../../components/architectSceneTable/useArchitectSceneListQuery';
import { useArchitectSceneSearch } from '../../components/architectSceneTable/useArchitectSceneSearch';
import useFilters from '../../components/recordingTable/useFilters';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreState } from '../../reducers';
import { ArchitectSceneInfo } from '../../types';
import { SCENES_PER_PAGE } from '../../utils/constants';
import {
  createPaginationConfig,
  getRowActionsColumnOptions,
  hasUserSelectionOnTable,
} from '../../utils/tableUtils';

// MaterialTableWrapper (and MRT internals) need an MUI theme context that
// this app's Chakra-based theming doesn't provide - every other consumer of
// MaterialTableWrapper (SharedRecording, SampleRecording, etc.) wraps it the
// same way before using it.
const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<ArchitectSceneInfo>>(
    MaterialTableWrapper
  );

const FILTER_GROUPS = [{ title: 'CREATOR', filterKeys: ['created_by'] }];

const ROW_ACTIONS_COLUMN_SIZE = 230;
const BULK_DOWNLOAD_STAGGER_MS = 400;

const wait = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

type RowActionsProps = {
  row: MRT_Row<ArchitectSceneInfo>;
  editTitle: (scene: ArchitectSceneInfo) => void;
  downloadFiles: (scene: ArchitectSceneInfo) => void;
  isDownloading: boolean;
  publishScene: (scene: ArchitectSceneInfo) => void;
  isPublishing: boolean;
  unpublishScene: (scene: ArchitectSceneInfo) => void;
  isUnpublishing: boolean;
  copyPrivateLink: (scene: ArchitectSceneInfo) => void;
  copyPublicLink: (scene: ArchitectSceneInfo) => void;
};

const RowActions = ({
  row,
  editTitle,
  downloadFiles,
  isDownloading,
  publishScene,
  isPublishing,
  unpublishScene,
  isUnpublishing,
  copyPrivateLink,
  copyPublicLink,
}: RowActionsProps) => (
  <Box
    flexWrap="nowrap"
    display="flex"
    data-testid="architect-scenes-row-action-buttons"
  >
    <RowActionButton
      title="Edit title"
      icon={<Icon icon="edit" />}
      row={row.original}
      onClickHandler={editTitle}
    />
    <RowActionButton
      title="Download files"
      icon={<Icon icon="download" />}
      row={row.original}
      onClickHandler={downloadFiles}
      isDisabled={isDownloading}
    />
    <RowActionButton
      title="Copy private link"
      icon={<Icon icon="contentCopy" />}
      row={row.original}
      onClickHandler={copyPrivateLink}
    />
    {row.original.publicURL ? (
      <RowActionButton
        title="Unpublish scene"
        icon={
          <MdOutlineUnpublished data-testid="architect-scene-unpublish-icon" />
        }
        row={row.original}
        onClickHandler={unpublishScene}
        isDisabled={isUnpublishing}
      />
    ) : (
      <RowActionButton
        title="Publish scene"
        icon={
          <MdOutlinePublishedWithChanges data-testid="architect-scene-publish-icon" />
        }
        row={row.original}
        onClickHandler={publishScene}
        isDisabled={isPublishing}
      />
    )}
    {row.original.publicURL && (
      <RowActionButton
        title="Copy public link"
        icon={<Icon icon="public" />}
        row={row.original}
        onClickHandler={copyPublicLink}
      />
    )}
  </Box>
);

type EditTitleModalProps = {
  scene: ArchitectSceneInfo;
  isSaving: boolean;
  onClose: () => void;
  onSave: (title: string) => Promise<void>;
};

type PublishSceneModalProps = {
  orgName: string;
  isPublishing: boolean;
  onClose: () => void;
  onPublish: () => Promise<void>;
};

const downloadSignedFile = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  link.style.display = 'none';
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.parentNode?.removeChild(link);
  }
};

const EditTitleModal = ({
  scene,
  isSaving,
  onClose,
  onSave,
}: EditTitleModalProps) => {
  const currentTitle = scene.title ?? '';
  const [title, setTitle] = useState(currentTitle);
  const trimmedTitle = title.trim();
  const canSave = !!trimmedTitle && trimmedTitle !== currentTitle.trim();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canSave && !isSaving) {
      void onSave(trimmedTitle);
    }
  };

  return (
    <ModalContainer
      isOpen
      onClose={onClose}
      closeable={!isSaving}
      header="Edit scene title"
      content={
        <Box minW="480px">
          <form
            onSubmit={handleSubmit}
            data-testid="architect-scene-edit-title-form"
          >
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <TextInput
                autoFocus
                value={title}
                maxLength={128}
                isReadOnly={isSaving}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setTitle(event.target.value)
                }
                data-testid="architect-scene-edit-title-input"
              />
            </FormControl>
            <Box mt={8} display="flex" justifyContent="flex-end" gap={3}>
              <Button
                variant="ghost"
                type="button"
                onClick={onClose}
                isDisabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isDisabled={!canSave || isSaving}
                isLoading={isSaving}
                data-testid="architect-scene-edit-title-save"
              >
                Save
              </Button>
            </Box>
          </form>
        </Box>
      }
    />
  );
};

const PublishSceneModal = ({
  orgName,
  isPublishing,
  onClose,
  onPublish,
}: PublishSceneModalProps) => (
  <ModalContainer
    isOpen
    onClose={onClose}
    closeable={!isPublishing}
    header="Publish scene"
    content={
      <Box
        w="560px"
        maxW="calc(100vw - 96px)"
        display="grid"
        gap={4}
        style={{ overflowWrap: 'anywhere' }}
      >
        <h6>This data is private to &quot;{orgName}&quot;</h6>
        <Box lineHeight="1.5">
          Only members of the organization can visualize this scene. To make it
          accessible to people outside the organization, generate a public link
          using the button below.
        </Box>
        <Box lineHeight="1.5">
          After generating a public link, people outside the organization will
          be able to view the scene but will not have permission to edit it.
          Make sure each filename has no confidential information.
        </Box>
        <Box mt={8} display="flex" justifyContent="flex-end" gap={3}>
          <Button
            variant="ghost"
            type="button"
            onClick={onClose}
            isDisabled={isPublishing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void onPublish()}
            isDisabled={isPublishing}
            isLoading={isPublishing}
            data-testid="architect-scene-publish-confirm"
          >
            Generate public link
          </Button>
        </Box>
      </Box>
    }
  />
);

export default function ArchitectScenesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useChakraHook();
  const { curUserOrg } = useStoreState((storeState) => storeState.user);
  const [sceneToEdit, setSceneToEdit] = useState<ArchitectSceneInfo | null>(
    null
  );
  const [sceneToPublish, setSceneToPublish] =
    useState<ArchitectSceneInfo | null>(null);
  const [downloadingSceneIds, setDownloadingSceneIds] = useState<
    Set<ArchitectSceneInfo['id']>
  >(() => new Set());
  const [unpublishingSceneIds, setUnpublishingSceneIds] = useState<
    Set<ArchitectSceneInfo['id']>
  >(() => new Set());
  const updateTitle = useUpdateArchitectSceneTitle(curUserOrg?.id);
  const publishScene = usePublishArchitectScene(curUserOrg?.id);
  const unpublishScene = useUnpublishArchitectScene(curUserOrg?.id);
  const sceneFileDownload = useDownloadArchitectSceneFiles(curUserOrg?.id);

  // Architect is currently limited to organization 1. Redirect anyone else
  // out of the page, matching the legacy behavior.
  useEffect(() => {
    if (curUserOrg && curUserOrg.id !== 1) {
      navigate('drive/private', { replace: true });
    }
  }, [curUserOrg, navigate]);

  const {
    filters,
    orderBy,
    sortOrder,
    pageIndex,
    isHydrated,
    setOrderBy,
    setPageIndex,
    setGroupFilters,
  } = useFilters('created_at', 'desc');

  const [sorting, setSorting] = useState<MRT_SortingState>(() => [
    {
      id: orderBy || 'created_at',
      desc: sortOrder ? sortOrder === 'desc' : true,
    },
  ]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex,
    pageSize: SCENES_PER_PAGE,
  });

  const isPaginationSynced = pagination.pageIndex === pageIndex;
  const isQueryEnabled = isHydrated && isPaginationSynced;

  const { searchTerm, setSearchTerm, debouncedSearchTerm } =
    useArchitectSceneSearch(
      new URLSearchParams(location.search).get('q') || ''
    );

  const { data, meta, isError, isRefetching, isLoading, refetch } =
    useArchitectSceneListQuery(
      curUserOrg,
      orderBy,
      sortOrder,
      pagination.pageIndex,
      pagination.pageSize,
      location.search,
      isQueryEnabled
    );

  const { filterOptions } = useArchitectSceneFilterOptions(curUserOrg?.id);

  // Sync search term to URL `q` param
  useEffect(() => {
    if (!isHydrated) return;

    const params = new URLSearchParams(location.search);
    const currentQ = params.get('q') || '';
    if (debouncedSearchTerm !== currentQ) {
      if (debouncedSearchTerm) {
        params.set('q', debouncedSearchTerm);
      } else {
        params.delete('q');
      }
      setPageIndex(0);
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, isHydrated, navigate]);

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [selectedColKeys, setSelectedColKeys] = useState<string[]>([]);
  const handleCloseFilterPopover = () => setFilterAnchorEl(null);

  const columns = useArchitectSceneColumns({
    filters,
    setFilterAnchorEl,
    setSelectedColKeys,
  });

  const openScene = (scene: ArchitectSceneInfo) => {
    if (isLoading) return;
    if (hasUserSelectionOnTable()) return;
    window.open(`/architect/${scene.id}`, '_blank', 'noopener,noreferrer');
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: '',
        description: 'Link copied to clipboard',
        status: 'success',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyPrivateLink = (scene: ArchitectSceneInfo) => {
    copyToClipboard(`${window.location.origin}/architect/${scene.id}`);
  };

  const copyPublicLink = (scene: ArchitectSceneInfo) => {
    if (scene.publicURL) {
      copyToClipboard(scene.publicURL);
    }
  };

  const saveTitle = async (title: string) => {
    if (!sceneToEdit) return;

    try {
      await updateTitle.mutateAsync({ sceneId: sceneToEdit.id, title });
      setSceneToEdit(null);
      showToast({
        title: '',
        description: 'Scene title updated',
        status: 'success',
      });
    } catch {
      showToast({
        title: '',
        description: 'Failed to update scene title',
        status: 'error',
      });
    }
  };

  const publishSelectedScene = async () => {
    if (!sceneToPublish) return;

    try {
      await publishScene.mutateAsync({
        sceneId: sceneToPublish.id,
        title: sceneToPublish.title ?? '',
      });
      setSceneToPublish(null);
      showToast({
        title: '',
        description: 'Scene published',
        status: 'success',
      });
    } catch {
      showToast({
        title: '',
        description: 'Failed to publish scene',
        status: 'error',
      });
    }
  };

  const handleUnpublishScene = async (scene: ArchitectSceneInfo) => {
    if (unpublishingSceneIds.has(scene.id)) return;

    setUnpublishingSceneIds((current) => new Set(current).add(scene.id));
    try {
      await unpublishScene.mutateAsync(scene.id);
      showToast({
        title: '',
        description: 'Scene unpublished',
        status: 'success',
      });
    } catch {
      showToast({
        title: '',
        description: 'Failed to unpublish scene',
        status: 'error',
      });
    } finally {
      setUnpublishingSceneIds((current) => {
        const next = new Set(current);
        next.delete(scene.id);
        return next;
      });
    }
  };

  const handleDownloadFiles = async (scene: ArchitectSceneInfo) => {
    if (downloadingSceneIds.has(scene.id)) return;

    setDownloadingSceneIds((current) => new Set(current).add(scene.id));
    try {
      let result: Awaited<ReturnType<typeof sceneFileDownload.mutateAsync>>;

      try {
        result = await sceneFileDownload.mutateAsync({ sceneId: scene.id });
      } catch {
        showToast({
          title: '',
          description: 'Failed to download scene file',
          status: 'error',
        });
        return;
      }

      if (result.files.length === 0 && result.failureCount === 0) {
        showToast({
          title: '',
          description: 'No scene files available',
          status: 'info',
        });
        return;
      }

      const downloadResults = await Promise.allSettled(
        result.files.map(async (file, index) => {
          await wait(index * BULK_DOWNLOAD_STAGGER_MS);
          downloadSignedFile(file.signedUrl, file.name);
        })
      );
      const failureCount =
        result.failureCount +
        downloadResults.filter(
          (downloadResult) => downloadResult.status === 'rejected'
        ).length;

      if (failureCount > 0) {
        showToast({
          title: '',
          description: `${failureCount} scene file${
            failureCount === 1 ? '' : 's'
          } could not be downloaded`,
          status: 'error',
        });
      }
    } finally {
      setDownloadingSceneIds((current) => {
        const next = new Set(current);
        next.delete(scene.id);
        return next;
      });
    }
  };

  const handlePaginationChange: MRT_TableOptions<ArchitectSceneInfo>['onPaginationChange'] =
    (updater) => {
      setPagination((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (next.pageIndex !== prev.pageIndex) {
          setPageIndex(next.pageIndex);
        }
        return next;
      });
    };

  const handleSortingChange: MRT_TableOptions<ArchitectSceneInfo>['onSortingChange'] =
    (updater) => {
      setSorting((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (!isHydrated || !next.length) {
          return next;
        }
        const { id: accessorKey, desc } = next[0];
        if (accessorKey && !accessorKey.startsWith('mrt-')) {
          setOrderBy(accessorKey, desc ? 'desc' : 'asc');
        }
        return next;
      });
    };

  useEffect(() => {
    if (!isHydrated) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPagination((prev) =>
      prev.pageIndex === pageIndex ? prev : { ...prev, pageIndex }
    );

    setSorting((prev) => {
      const current = prev[0];
      const nextDesc = sortOrder === 'desc';
      if (current?.id === orderBy && current?.desc === nextDesc) {
        return prev;
      }
      return [{ id: orderBy, desc: nextDesc }];
    });
  }, [isHydrated, orderBy, pageIndex, sortOrder]);

  const totalSelectedFilters = Object.values(filters).reduce(
    (total, values) => total + values.length,
    0
  );

  const clearAllFilters = () => {
    navigate({ pathname: location.pathname, search: '' });
  };

  if (!curUserOrg) return null;

  return (
    <Box
      p="25px 36px 0px"
      h="100%"
      overflow="hidden"
      position="relative"
      display="grid"
      gridTemplateRows="auto 1fr"
      gap="1rem"
      data-testid="architect-scenes-root"
    >
      <Box display="grid" rowGap="1rem">
        <Box
          display="flex"
          placeContent="space-between"
          alignItems="center"
          mb={6}
        >
          <H2 fontWeight="medium" fontSize={30}>
            Architect Scenes
          </H2>
          <Button
            variant="ghost"
            colorScheme="gray"
            gap={1}
            onClick={() => refetch()}
            data-testid="architect-scenes-refresh"
          >
            <MdRefresh size={18} /> Refresh List
          </Button>
        </Box>
        <Box
          display="flex"
          gap={2}
          alignItems="center"
          flexWrap="wrap"
          data-testid="architect-scenes-filters"
        >
          <InputGroup maxW="25ch" data-testid="architect-scenes-search-group">
            <InputLeftElement pointerEvents="none">
              <MdSearch size={18} />
            </InputLeftElement>
            <TextInput
              pl="2.5rem"
              pr="3.5rem"
              placeholder="Search ..."
              type="search"
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              data-testid="architect-scenes-search-input"
            />
            <InputRightElement>
              <IconButton
                onClick={() => setSearchTerm('')}
                data-testid="architect-scenes-search-clear"
              >
                <MdClear size={16} />
              </IconButton>
            </InputRightElement>
          </InputGroup>

          {FILTER_GROUPS.map((group) => (
            <FilterDropDown
              key={group.title}
              title={group.title}
              filterKey={group.filterKeys}
              filterOptions={filterOptions}
              selectedFilters={filters}
              setGroupFilters={setGroupFilters}
              data-testid="architect-scenes-filter-creator"
            />
          ))}
          {totalSelectedFilters > 0 && (
            <Button
              variant="ghost"
              colorScheme="gray"
              gap={1}
              onClick={clearAllFilters}
              fontSize="12px"
              data-testid="architect-scenes-clear-filters"
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Box>

      <Box
        overflow="hidden"
        position="relative"
        data-testid="architect-scenes-table-container"
      >
        <ThemedMaterialTableWrapper
          columns={columns}
          data={data}
          getRowId={(row: ArchitectSceneInfo) => String(row.id)}
          manualPagination
          enableFilters={false}
          enableColumnActions
          enableTopToolbar={false}
          enableRowActions
          enableSortingRemoval={false}
          layoutMode="grid"
          muiTableContainerProps={{
            sx: { maxHeight: 'calc(100vh - 320px)' },
            style: { tableLayout: 'auto' },
          }}
          muiPaginationProps={{
            rowsPerPageOptions: [50, 100, 200, 300, 1000],
          }}
          renderRowActions={({ row }) => (
            <RowActions
              row={row}
              editTitle={setSceneToEdit}
              downloadFiles={(scene) => void handleDownloadFiles(scene)}
              isDownloading={downloadingSceneIds.has(row.original.id)}
              publishScene={setSceneToPublish}
              isPublishing={
                publishScene.isPending && sceneToPublish?.id === row.original.id
              }
              unpublishScene={(scene) => void handleUnpublishScene(scene)}
              isUnpublishing={unpublishingSceneIds.has(row.original.id)}
              copyPrivateLink={copyPrivateLink}
              copyPublicLink={copyPublicLink}
            />
          )}
          displayColumnDefOptions={getRowActionsColumnOptions(
            ROW_ACTIONS_COLUMN_SIZE,
            { header: 'ACTIONS' }
          )}
          initialState={{
            columnPinning: { right: ['mrt-row-actions'] },
          }}
          onPaginationChange={handlePaginationChange}
          onSortingChange={handleSortingChange}
          rowCount={meta?.totalCount ?? 0}
          state={{
            isLoading,
            pagination,
            sorting,
            showAlertBanner: isError,
            showProgressBars: isLoading || isRefetching,
            showSkeletons: isLoading || isRefetching,
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => openScene(row.original),
            sx: { cursor: 'pointer' },
            'data-testid': `architect-scenes-row-${row.index}`,
          })}
          {...createPaginationConfig(meta, pagination)}
        />
      </Box>

      {filterAnchorEl && (
        <ColumnFilterPopover
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilterPopover}
          filterKey={selectedColKeys}
          filterOptions={filterOptions}
          selectedFilters={filters}
          setGroupFilters={setGroupFilters}
          data-testid="architect-scenes-column-filter-popover"
        />
      )}

      {sceneToEdit && (
        <EditTitleModal
          key={sceneToEdit.id}
          scene={sceneToEdit}
          isSaving={updateTitle.isPending}
          onClose={() => setSceneToEdit(null)}
          onSave={saveTitle}
        />
      )}

      {sceneToPublish && (
        <PublishSceneModal
          key={sceneToPublish.id}
          orgName={curUserOrg.name}
          isPublishing={publishScene.isPending}
          onClose={() => setSceneToPublish(null)}
          onPublish={publishSelectedScene}
        />
      )}
    </Box>
  );
}
